/**
 * 실행계획 위에 「문제를 지적한 줄」 을 짚고 번호를 단다 — 진단(D1…)이 계획의 어디를 말하는지 한눈에 보이게(사용자 제안 2026-10-08).
 *
 * 그림이 아니라 원문 위에 겹친다 — 복사·확대·다크 모드가 그대로 되고, 리포트(md)도 같은 표시(줄 끝 ◀ D1)를 싣는다.
 * 줄 Id 는 표의 첫 칸(`|*  4 |`)에서 읽는다. 한글 이름 줄이 밀리지 않게 넓은 글자는 2칸 상자에(utils/monoWide).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { monoWide } from '../../utils/monoWide';
import './PlanMarked.css';

const LINE_ID = /^\|\*?\s*(\d+)\s*\|/;

export function planLineId(line) {
  const m = LINE_ID.exec(String(line || ''));
  return m ? Number(m[1]) : null;
}

/**
 * @param planText 실행계획 원문
 * @param marks    Map<줄 Id, string[]>  줄마다 단 번호(D1…)
 * @param active   지금 강조할 번호(카드에 마우스를 올린 것)
 * @param onPick   (번호) => void — 번호를 누르면 그 카드로
 */
function PlanMarked({ planText, marks, active, onPick }) {
  const { t } = useTranslation();
  const lines = String(planText || '').split('\n');
  return (
    <pre className="stn-pre stn-planmarked" aria-label={t('common:stn.planMarked')}>
      {lines.map((line, i) => {
        const id = planLineId(line);
        const labels = id != null ? (marks.get(id) || []) : [];
        const on = labels.length > 0;
        const hot = on && active && labels.includes(active);
        return (
          // eslint-disable-next-line react/no-array-index-key -- 줄 순서가 곧 키다
          <span key={i} className={`stn-pl${on ? ' marked' : ''}${hot ? ' hot' : ''}`}>
            {monoWide(line, 'mono-wide')}
            {on && (
              <span className="stn-pl-badges">
                {labels.map(l => (
                  <button key={l} type="button" className={`stn-pl-badge${l.startsWith('N') ? ' note' : ''}${active === l ? ' hot' : ''}`} onClick={() => onPick && onPick(l)}
                    title={t('common:stn.planMarkGo', { id: l })}>{l}</button>
                ))}
              </span>
            )}
            {'\n'}
          </span>
        );
      })}
    </pre>
  );
}

PlanMarked.propTypes = {
  planText: PropTypes.string,
  marks: PropTypes.instanceOf(Map).isRequired,
  active: PropTypes.string,
  onPick: PropTypes.func,
};

/**
 * 문서(.md)에 실린 표시 줄 — 리포트의 `…  ◀ D1, N1`, 사례 문서의 `…  ◀ [1][3]` — 를 떼어 낸다(글은 그대로 두고 그릴 때만).
 * @returns { text, labels } — 표시가 없으면 labels 는 빈 배열
 */
const MARK_TAIL = /\s+◀\s+((?:\[\d+\]|[A-Z]\d+)(?:\s*,?\s*(?:\[\d+\]|[A-Z]\d+))*)\s*$/;
export function splitMarkedLine(line) {
  const m = MARK_TAIL.exec(String(line || ''));
  if (!m) return { text: line, labels: [] };
  return { text: line.slice(0, m.index), labels: m[1].match(/\[\d+\]|[A-Z]\d+/g).map(l => l.replace(/[[\]]/g, '')) };
}

/** 표시 줄이 있는 글인가 — 문서 코드 블록을 이 모양으로 그릴지 고를 때 */
export function hasMarkedLines(text) {
  return String(text || '').split('\n').some(l => splitMarkedLine(l).labels.length > 0);
}

/**
 * 문서 속 실행계획(코드 블록)을 진단 탭과 같은 모양으로 — 표시한 줄은 강조, 줄 끝 번호는 배지(누르지 않는 정적 배지).
 * D… = 진단(주황), N… = 참고 사항(회색), 숫자 = 사례 본문·유의사항의 설명 번호(주황).
 */
export function MarkedPlanText({ text }) {
  const lines = String(text || '').split('\n');
  return lines.map((line, i) => {
    const { text: body, labels } = splitMarkedLine(line);
    const on = labels.length > 0;
    return (
      // eslint-disable-next-line react/no-array-index-key -- 줄 순서가 곧 키다
      <span key={i} className={`stn-pl${on ? ' marked' : ''}`}>
        {monoWide(body, 'mono-wide')}
        {on && (
          <span className="stn-pl-badges">
            {labels.map(l => <span key={l} className={`stn-pl-badge static${l.startsWith('N') ? ' note' : ''}`}>{l}</span>)}
          </span>
        )}
        {i < lines.length - 1 ? '\n' : null}
      </span>
    );
  });
}
MarkedPlanText.propTypes = { text: PropTypes.string };

export default PlanMarked;
